import { useEffect, useRef, useState } from 'react'

export type CardReaderPayload = {
  profile?: string
  prefixId?: string
  firstname?: string
  lastname?: string
  firstnameEn?: string
  lastnameEn?: string
  idCard?: string
  gender?: string
  dateOfBirth?: string
  address?: string
  soi?: string
  road?: string
  province?: string
  district?: string
  subDistrict?: string
  signature?: string
}

export type CardReaderStatus = 'idle' | 'connecting' | 'connected' | 'error'

export type CardReaderError = {
  code?: string
  message?: string
}

export type UseCardReaderOptions = {
  enabled: boolean
  onCardData: (payload: CardReaderPayload) => void
  onError?: (error: CardReaderError) => void
}

const DEFAULT_PORT_WSS = 9900
const DEFAULT_PORT_WS = 9899

const CARD_FIELD_KEYS: (keyof CardReaderPayload)[] = [
  'profile', 'prefixId', 'firstname', 'lastname', 'firstnameEn', 'lastnameEn',
  'idCard', 'gender', 'dateOfBirth', 'address', 'soi', 'road',
  'province', 'district', 'subDistrict', 'signature',
]

const getAgentUrls = (): string[] => {
  const override = (window as any).REACT_APP_CARD_READER_WS as string | string[] | undefined
  if (override) {
    return Array.isArray(override)
      ? override
      : String(override).split(',').map((s) => s.trim()).filter(Boolean)
  }
  return window.location.protocol === 'https:'
    ? [`wss://localhost:${DEFAULT_PORT_WSS}`, `ws://localhost:${DEFAULT_PORT_WS}`]
    : [`ws://localhost:${DEFAULT_PORT_WS}`]
}

const looksLikeCardData = (obj: any): boolean => {
  if (!obj || typeof obj !== 'object') return false
  return CARD_FIELD_KEYS.some((k) => Object.prototype.hasOwnProperty.call(obj, k))
}

type ParseResult =
  | { kind: 'data'; payload: CardReaderPayload }
  | { kind: 'error'; error: CardReaderError }
  | { kind: 'noise' }

// Accepts every shape the agent has historically sent so older builds that
// haven't installed the new CliniterCardID still work:
//   - { event: 'card-data', ...payload }   new spec (FRONTEND_INTEGRATION.md §9)
//   - { event: 'reader-connected' | ... }  noise events from the new agent
//   - { status: 'success', data: payload } optional wrapper (§6.2)
//   - { status: 'error', code, message }   error wrapper (§6.2)
//   - { ...payload }                       legacy flat JSON, no event marker
const parseAgentMessage = (msg: any): ParseResult => {
  if (!msg || typeof msg !== 'object') return { kind: 'noise' }

  if (msg.status === 'error') {
    return { kind: 'error', error: { code: msg.code, message: msg.message } }
  }
  if (msg.status === 'success' && looksLikeCardData(msg.data)) {
    return { kind: 'data', payload: msg.data }
  }
  if (msg.event === 'card-data') {
    const { event: _evt, ...rest } = msg
    return { kind: 'data', payload: rest }
  }
  if (msg.event) {
    return { kind: 'noise' }
  }
  if (looksLikeCardData(msg)) {
    return { kind: 'data', payload: msg }
  }
  return { kind: 'noise' }
}

export const useCardReader = ({ enabled, onCardData, onError }: UseCardReaderOptions) => {
  const [status, setStatus] = useState<CardReaderStatus>('idle')
  const onCardDataRef = useRef(onCardData)
  const onErrorRef = useRef(onError)
  onCardDataRef.current = onCardData
  onErrorRef.current = onError

  useEffect(() => {
    if (!enabled) {
      setStatus('idle')
      return
    }
    const urls = getAgentUrls()
    if (!urls.length) {
      setStatus('error')
      return
    }

    let idx = 0
    let ws: WebSocket | null = null
    let closedByUser = false

    const tryNext = () => {
      if (closedByUser) return
      if (idx >= urls.length) {
        setStatus('error')
        return
      }
      const url = urls[idx++]
      setStatus('connecting')
      try {
        ws = new WebSocket(url)
      } catch {
        tryNext()
        return
      }
      ws.onopen = () => setStatus('connected')
      ws.onmessage = (e) => {
        let parsed: any
        try { parsed = JSON.parse(e.data) } catch { return }
        const result = parseAgentMessage(parsed)
        if (result.kind === 'data') onCardDataRef.current(result.payload)
        else if (result.kind === 'error') onErrorRef.current?.(result.error)
      }
      ws.onerror = () => { /* handled in onclose */ }
      ws.onclose = () => {
        if (closedByUser) return
        if (idx < urls.length) tryNext()
        else setStatus('error')
      }
    }
    tryNext()

    return () => {
      closedByUser = true
      try { ws?.close() } catch { /* ignore */ }
    }
  }, [enabled])

  return { status }
}

export const dataUrlToFile = (dataUrl: string, filename: string): File | null => {
  if (!dataUrl || typeof dataUrl !== 'string') return null
  const [meta, base64] = dataUrl.split(',')
  if (!meta || !base64) return null
  const mimeMatch = /data:([^;]+);base64/.exec(meta)
  const mime = mimeMatch?.[1] || 'image/jpeg'
  try {
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return new File([bytes], filename, { type: mime })
  } catch {
    return null
  }
}
